import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useAppContext } from "../../../context/AuthContext";
import { UserController, UserControllerType } from "../../../controller/user/UserController";
import LabelsContext from "../../../lang/LabelsContext";
import PublicService, { PublicServiceType } from "../../../service/public/PublicService";
import MessageType from "../../../utils/MessageType";
import { handleErrorMessage, useEffectOnce } from "../../../utils/Utils";
import EnumGroup from "../../../utils/enum/EnumGroup";

interface UsersServiceType {
  users: any;
  setUsers: any;
  countryList: any;
  saveReviewer: Function;
  loginAs: Function;
  getUserById: Function;
  userEdit: any;
  getGroups: Function;
  groups: any;
  updateUser: Function;
  changeGroupDialog: any;
  setChangeGroupDialog: any;
  changeGroup: any;
  authData: any;
}

export default function UsersService(): UsersServiceType {
  const { authData, showMessage, deleteAuthData, updateAuthData, setBlockUI } = useAppContext();
  const { Labels } = LabelsContext();
  const history = useHistory();
  const {
    axiosGetUsers,
    axiosSaveReviewer,
    axiosLoginAs,
    axiosGetUserById,
    axiosGetGroups,
    axiosUpdateUser,
    axiosPostUpdateGroup,
  }: UserControllerType = UserController();
  const { getCountryList, countryList }: PublicServiceType = PublicService();
  const [users, setUsers] = useState<any>([]);
  const [groups, setGroups] = useState<any>([]);
  const [userEdit, setUserEdit] = useState<any>(null);
  const [changeGroupDialog, setChangeGroupDialog] = useState<any>();

  useEffectOnce(() => {
    getCountryList();
    getUsers();
    getGroups();
  });

  const getUsers = () => {
    setBlockUI(true);
    axiosGetUsers()
      .then((result: any) => {
        setUsers(result.data);
        setBlockUI(false);
      })
      .catch((error: any) => {
        setBlockUI(false);
        handleErrorMessage(showMessage, error);
      });
  };

  const getGroups = () => {
    axiosGetGroups()
      .then((result: any) => {
        if (authData?.user.group?.group === EnumGroup.ASSOCIATE_EDITOR) {
          setGroups(
            result.data.filter((g: any) => [EnumGroup.REVIEWER, EnumGroup.GUEST, EnumGroup.AUTHOR].includes(g.group))
          );
        } else {
          setGroups(result.data.filter((g: any) => ![EnumGroup.EDITOR].includes(g.group)));
        }
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const getUserById = (id: string) => {
    axiosGetUserById(id)
      .then((result: any) => {
        setUserEdit(result.data);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const saveReviewer = (reviewer: any) => {
    axiosSaveReviewer(reviewer)
      .then((response: any) => {
        getUsers();
        showMessage(Labels.SUCCESSFULLY_ADDED, MessageType.SUCCESS);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const changeGroup = () => {
    axiosPostUpdateGroup(changeGroupDialog.id, changeGroupDialog.group.id)
      .then((response: any) => {
        showMessage(Labels.SUCCESSFULLY_UPDATED, MessageType.SUCCESS);
        getUsers();
        setChangeGroupDialog(undefined);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const updateUser = (id: any, updateUser: any) => {
    let parseUser = { ...updateUser };
    parseUser.country = updateUser?.country ? updateUser.country.countryName : null;
    parseUser.groupId = updateUser?.group ? updateUser.group.id : null;
    axiosUpdateUser(id, parseUser)
      .then((response: any) => {
        history.push("/users");
        getUsers();
        showMessage(Labels.SUCCESSFULLY_UPDATED, MessageType.SUCCESS);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  const loginAs = (id: any) => {
    axiosLoginAs(id)
      .then((response: any) => {
        deleteAuthData();
        updateAuthData(response.data);
        history.push("/");
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  return {
    users,
    setUsers,
    countryList,
    saveReviewer,
    loginAs,
    getUserById,
    userEdit,
    getGroups,
    groups,
    updateUser,
    changeGroupDialog,
    setChangeGroupDialog,
    changeGroup,
    authData,
  };
}

export type { UsersServiceType };
